/**
 * Foreword:
 *
 * Technically, a plugin system is "going backwards" to older tanstack table before it was modular.
 * Our concern here is different from tanstack table hook - which optimises for all use cases with 0 limits.
 *
 * We instead have a specific set of features that we want to build the design system DataTable component with.
 *
 * "Going back" to plugins is then natural since our goal is to add limits and make the usage of DataTable
 * as easy as possible for people building tables in apps like Foam
 *
 *
 * At the same time, we have full configurability in terms of UI rendering since the columns depend on users.
 */
/** biome-ignore-all lint/suspicious/noExplicitAny: needed */

import type { UseInfiniteQueryResult } from "@tanstack/react-query"
import {
  type Cell,
  type ExpandedState,
  getExpandedRowModel,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  type RowData as TanStackRowData,
  type VisibilityState,
} from "@tanstack/react-table"
import * as React from "react"
import { Checkbox } from "../Checkbox/Checkbox"
import { InfiniteScrollTrigger } from "./InfiniteScrollTrigger"
import { InitialSkeleton } from "./InitialSkeleton"
import { dtColumnHelper, type UseDataTablePlugin } from "./types"

/**
 * This action type object can also be used as an "at-a-glance" view of all features our table has!
 */
export type PluginActionTypes<TRowData extends TanStackRowData, TQueryData = unknown> = {
  expansionActions: ReturnType<typeof useRowExpansion<TRowData>>["actions"]
  rowSelectionActions: ReturnType<typeof useRowSelection<TRowData>>["actions"]
  columnVisibilityActions: ReturnType<typeof useColumnVisibility<TRowData>>["actions"]
  sortingActions: ReturnType<typeof useSorting<TRowData>>["actions"]
  paginationActions: ReturnType<typeof useEndlessPagination<TRowData, TQueryData>>["actions"]
}

/**
 * ####################################
 *           PLUGIN HOOKS
 * ####################################
 */

/**
 * Row Selection Plugin
 */
export function useRowSelection<TRowData extends TanStackRowData>({
  getRowId,
  initialRowSelection = {},
  onRowSelectionChange,
}: {
  getRowId?: (rowData: TRowData) => string
  initialRowSelection?: RowSelectionState
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void
} = {}) {
  const { state, actions } = makeRowSelectionStore(initialRowSelection, {
    onRowSelectionChange,
  })

  return {
    rowSelection: state.rowSelection,
    actions,
    withSelectionColumn,
    plugin: {
      options: {
        enableRowSelection: true,
        getRowId,
        onRowSelectionChange: actions.handleRowSelectionChange,
        initialState: {
          columnPinning: {
            left: ["__select__"],
          },
        },
      },
      state: {
        rowSelection: state.rowSelection,
      },
      getCellProps: (cell: Cell<TRowData, unknown>) => {
        if (cell.column.id === "__select__") {
          return {
            className: "z-[12]", // Loading bar has z-11 and tbody has z-10
          }
        }
      },
    } as UseDataTablePlugin<TRowData>,
  }
}

/**
 * Column Visibility Plugin
 */
export function useColumnVisibility<TRowData extends TanStackRowData>({
  initialColumnVisibility = {},
}: {
  initialColumnVisibility?: VisibilityState
} = {}) {
  const { state, actions } = makeColumnVisibilityStore(initialColumnVisibility)

  return {
    actions,
    plugin: {
      options: {
        enableColumnVisibility: true,
        onColumnVisibilityChange: actions.handleColumnVisibilityChange,
      },
      state: {
        columnVisibility: state.columnVisibility,
      },
      // } as UseDataTablePlugin<TRowData>,
    } as UseDataTablePlugin<TRowData>,
  }
}

/**
 * Column Highlight Plugin
 */
export function useColumnHighlight<TRowData extends TanStackRowData>({
  initialColumnHighlight = [],
}: {
  initialColumnHighlight?: string[]
} = {}) {
  const { state, actions } = makeColumnHighlightStore(initialColumnHighlight)

  return {
    actions,
    plugin: {
      state: {
        columnHighlight: state.columnHighlight,
      },
      options: {
        onColumnHighlightChange: actions.handleColumnHighlightChange,
      },
      getCellProps: (cell: Cell<TRowData, unknown>) => {
        if (state.columnHighlight.includes(cell.column.id)) {
          return {
            className:
              "[&:not(:hover)&:not([data-state=selected])]:bg-[--wb-table-cell-alternate-background-color]",
          }
        }
        return {}
      },
    } as UseDataTablePlugin<TRowData>,
  }
}

/**
 * Sorting Plugin (Server-Side Sorting By Default)
 *
 * - in the future we can make plugins/separate component for client-side table ops, currently out of scope
 */
export function useSorting<TRowData extends TanStackRowData>({
  initialSorting = [],
}: {
  initialSorting?: SortingState
} = {}) {
  const { state, actions } = makeSortingStore(initialSorting)

  return {
    sorting: state.sorting,
    actions,
    plugin: {
      options: {
        manualSorting: true,
        onSortingChange: actions.handleSortingChange,
      },
      state: {
        sorting: state.sorting,
      },
    } as UseDataTablePlugin<TRowData>,
  }
}

/**
 * Pagination Plugin (Designed for Infinite Scrolling)
 *
 * Works with external useInfiniteQuery hooks. The application provides:
 * - fetchNextPage: function to fetch next page of data
 * - hasNextPage: boolean indicating if more data available
 * - isFetchingNextPage: boolean indicating if currently fetching
 * - totalFetched: number of total items currently loaded
 * - totalRowCount: total number of rows available
 */
const DEFAULT_PAGE_SIZE = 50

export function useEndlessPagination<TRowData extends TanStackRowData, TQueryData>({
  infiniteQueryReturn,
  getItemsFromPage,
  pageSize = DEFAULT_PAGE_SIZE,
  initialPageIndex = 0,
}: {
  infiniteQueryReturn: UseInfiniteQueryResult<TQueryData, unknown>
  getItemsFromPage: (page: TQueryData) => TRowData[]
  pageSize?: number
  initialPageIndex?: number
}) {
  const allItems = React.useMemo(
    () => infiniteQueryReturn.data?.pages?.flatMap((page) => getItemsFromPage(page)) ?? [],
    [infiniteQueryReturn.data, getItemsFromPage]
  )

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isInitialLoading, isFetching } =
    infiniteQueryReturn

  const { state, actions } = makePaginationStore({
    pageSize,
    initialPageIndex,
    fetchNextPage,
  })

  const plugin: UseDataTablePlugin<TRowData> = {
    options: {
      manualPagination: true,
    },
    state: {
      pagination: {
        pageIndex: state.pageIndex,
        pageSize: state.pageSize,
      },
    },
    elements: {
      bottomBar: (
        <InfiniteScrollTrigger
          onFetchNext={actions.nextPage}
          isFetching={isFetchingNextPage}
          hasNextPage={Boolean(hasNextPage)}
          triggerOffset={300}
        />
      ),
      bodySpinner: isInitialLoading ? <InitialSkeleton /> : undefined,
    },
    config: {
      shouldShowProgressIndicator: isFetching,
    },
  }

  return {
    pageIndex: state.pageIndex,
    pageSize: state.pageSize,
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    isInitialLoading,
    isFetching,
    actions,
    allItems,
    plugin,
  }
}

/**
 * Expanding Plugin
 */
export function useRowExpansion<TRowData extends TanStackRowData>({
  ExpandedContent,
  initialExpanded = {},
  isSticky = false,
}: {
  ExpandedContent: React.ComponentType<{
    actions: PluginActionTypes<TRowData>["expansionActions"]
  }>
  initialExpanded?: ExpandedState
  isSticky?: boolean
}) {
  const { state, actions } = makeExpandingStore(initialExpanded)

  const onToggleExpand = React.useCallback(
    (rowId: string) => {
      actions.handleExpandedChange((prev) => {
        if (prev === true) {
          return { [rowId]: false }
        } else {
          const currentState = prev as Record<string, boolean>
          const isCurrentlyExpanded = !!currentState[rowId]
          return { [rowId]: !isCurrentlyExpanded }
        }
      })
    },
    [actions]
  )

  const updatedActions = React.useMemo(
    () => ({
      ...actions,
      onToggleExpand,
    }),
    [actions, onToggleExpand]
  )

  return {
    expanded: state.expanded,
    actions: updatedActions,
    plugin: {
      elements: {
        expandedRow: <ExpandedContent actions={updatedActions} />,
      },
      options: {
        getExpandedRowModel: getExpandedRowModel(),
        onExpandedChange: actions.handleExpandedChange,
      },
      state: {
        expanded: state.expanded,
      },
      config: {
        isExpandedRowSticky: isSticky,
      },
    } as UseDataTablePlugin<TRowData>,
  }
}

/**
 * ##############################################
 *           PLUGIN PROXY STORE FACTORIES
 * #############################################
 */

function makeRowSelectionStore(
  initialRowSelection: RowSelectionState = {},
  options: {
    onRowSelectionChange?: (rowSelection: RowSelectionState) => void
  } = {}
) {
  const [selectionState, setSelectionState] = React.useState({
    rowSelection: initialRowSelection,
  })

  const handleRowSelectionChange = createStateHandler(
    () => selectionState.rowSelection,
    (newValue) => {
      setSelectionState((prev) => ({
        ...prev,
        rowSelection: newValue,
      }))
      options.onRowSelectionChange?.(newValue)
    }
  )

  const resetRowSelection = () => {
    setSelectionState({
      rowSelection: {},
    })
  }

  return {
    state: selectionState,
    actions: {
      handleRowSelectionChange,
      resetRowSelection,
    },
  }
}

function makeColumnVisibilityStore(initialColumnVisibility: VisibilityState = {}) {
  const [columnVisibilityState, setColumnVisibilityState] = React.useState({
    columnVisibility: initialColumnVisibility,
  })

  const handleColumnVisibilityChange = createStateHandler(
    () => columnVisibilityState.columnVisibility,
    (newValue) => {
      setColumnVisibilityState((prev) => ({
        ...prev,
        columnVisibility: newValue,
      }))
    }
  )

  const resetColumnVisibility = () => {
    setColumnVisibilityState({
      columnVisibility: {},
    })
  }

  return {
    state: columnVisibilityState,
    actions: {
      handleColumnVisibilityChange,
      resetColumnVisibility,
    },
  }
}

export function makeSortingStore(initialSorting: SortingState = []) {
  const [sortingState, setSortingState] = React.useState({
    sorting: initialSorting,
  })

  const handleSortingChange = createStateHandler(
    () => sortingState.sorting,
    (newValue) => {
      setSortingState((prev) => ({
        ...prev,
        sorting: newValue,
      }))
    }
  )

  const resetSorting = () => {
    setSortingState({
      sorting: [],
    })
  }

  return {
    state: sortingState,
    actions: {
      handleSortingChange,
      resetSorting,
    },
  }
}

function makeColumnHighlightStore(initialColumnHighlight: string[] = []) {
  const [columnHighlightState, setColumnHighlightState] = React.useState({
    columnHighlight: initialColumnHighlight,
  })

  const handleColumnHighlightChange = createStateHandler(
    () => columnHighlightState.columnHighlight,
    (newValue) => {
      setColumnHighlightState((prev) => ({
        ...prev,
        columnHighlight: newValue,
      }))
    }
  )

  return {
    state: columnHighlightState,
    actions: { handleColumnHighlightChange },
  }
}

function makePaginationStore({
  pageSize,
  initialPageIndex,
  fetchNextPage,
}: {
  pageSize: number
  initialPageIndex: number
  fetchNextPage?: () => void
}) {
  const [pageIndex, setPageIndex] = React.useState(initialPageIndex)
  const [currentPageSize, setCurrentPageSize] = React.useState(pageSize)

  const nextPage = React.useCallback(() => {
    if (fetchNextPage) {
      setPageIndex((prev) => prev + 1)
      fetchNextPage()
    }
  }, [fetchNextPage])

  const resetPagination = React.useCallback(() => {
    setPageIndex(0)
  }, [])

  const setPageSize = React.useCallback((newPageSize: number) => {
    setCurrentPageSize(newPageSize)
  }, [])

  return {
    state: {
      pageIndex,
      pageSize: currentPageSize,
    },
    actions: {
      nextPage,
      resetPagination,
      setPageSize,
    },
  }
}

function makeExpandingStore(initialExpanded: ExpandedState = {}) {
  const [expandedState, setExpandedState] = React.useState({
    expanded: initialExpanded,
  })

  const handleExpandedChange: OnChangeFn<ExpandedState> = React.useCallback((value) => {
    setExpandedState((prev) => {
      const newValue = typeof value === "function" ? value(prev.expanded) : value
      return { expanded: newValue }
    })
  }, [])

  const resetExpanded = React.useCallback(() => {
    setExpandedState({ expanded: {} })
  }, [])

  return {
    state: expandedState,
    actions: {
      handleExpandedChange,
      resetExpanded,
    },
  }
}

/**
 * ####################################
 *           UTILITY FUNCTIONS
 * ####################################
 */

/**
 * Creates a state change handler that handles both function and direct value updates
 */
function createStateHandler<T>(
  getCurrentValue: () => T,
  setValue: (newValue: T) => void
): OnChangeFn<T> {
  return (value) => {
    if (typeof value === "function") {
      const newValue = (value as (old: T) => T)(getCurrentValue())
      setValue(newValue)
    } else {
      setValue(value)
    }
  }
}

/**
 * Helper to inject the selection column
 */
export function withSelectionColumn<Cols extends any[]>(columns: Cols) {
  const [columnHelper] = dtColumnHelper<any, string>()
  return [
    columnHelper.display({
      id: "__select__",
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onChange={(e) => row.toggleSelected(e.target.checked)}
          aria-label="Select row"
        />
      ),
      size: 32,
    }),
    ...columns,
  ] as unknown as Cols
}
